import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';
import { MenuStateService } from '../../../_services/app/menu-state.service';
import { AgentDetail } from '../../../_models/agent.model';
import { AgentAdminService } from '../../../_services/api/agent-admin.service';
import { AdminUserService } from '../../../_services/api/admin-user.service';
import { AdminAgentReservationsCard } from '../admin-agent-reservations-card/admin-agent-reservations-card';

@Component({
  selector: 'app-admin-agent-details',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminAgentReservationsCard],
  templateUrl: './admin-agent-details.html',
  styleUrl: './admin-agent-details.scss',
})
export class AdminAgentDetails implements OnInit {
  agentId!: number;
  agent: AgentDetail | null = null;
  isLoading = false;
  errorMessage: string | null = null;

  menuOpen = false;
  showDeleteModal = false;
  isUpdatingVisibility = false;

  showAvailabilityModal = false;
  isUpdatingAvailability = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private agentService: AgentAdminService,
     private userService: AdminUserService,
    private notification: NotificationService,
    private elementRef: ElementRef, 
    private menuStateService: MenuStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {    
    this.loadAgentId();
    if(this.agentId){
      this.menuStateService.activeMenu$.subscribe(activeId => {
        this.menuOpen = activeId === this.agentId;
      });
      if (isPlatformBrowser(this.platformId)) {
          document.addEventListener('click', this.closeMenu.bind(this));
      }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
        document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.agentId);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  setDefaultImage(event: any): void {
    event.target.src = './assets/images/user-default.png';
  }

  private loadAgentId(): void {
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      
      if (id) {
        this.agentId = +id;
        this.loadAgentDetails();
      } else {
        this.errorMessage = 'ID d\'agent non trouvé';
        this.goToList();
      }
    });
  }

  private loadAgentDetails(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.agentService.getAgentDetails(this.agentId).subscribe({
      next: (response: ApiResponse<AgentDetail>) => {
        if (response.success && response.data) {
          this.agent = response.data;
        } else {
          this.errorMessage = response.message || 'Erreur lors du chargement des données';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading agent details:', error);
        this.errorMessage = 'Erreur de chargement des détails d\'agent';
        this.isLoading = false;
        
        if (error.status === 404) {
          this.errorMessage = 'Agent non trouvé';
          setTimeout(() => this.goToList(), 2000);
        }
      }
    });
  }

  goToList(): void {
    this.router.navigate(['/admin/agents']);
  }

  formatAddress(address: any): string {
    if (!address) return 'Non spécifiée';
    
    const parts: string[] = [];
    
    if (address.streetNumber && address.street) {
      parts.push(`${address.street} ${address.streetNumber}`);
    } else if (address.street) {
      parts.push(address.street);
    }
    
    if (address.postalCode && address.city) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else if (address.postalCode) {
      parts.push(address.postalCode);
    } else if (address.city) {
      parts.push(address.city);
    }
    
    if (parts.length === 0) {
      return 'Non spécifiée';
    }
    
    return parts.join(', ') + ".";
  }

  toggleVisibility(): void {
    if (!this.agent) return;
    if (this.isUpdatingVisibility) return;
    
    this.isUpdatingVisibility = true;
    this.menuStateService.setActiveMenu(null);
    this.menuOpen = false;
    
    if (this.agent.isEnabled) {
      this.userService.disableUser(this.agent.id).subscribe({
        next: () => {
          this.agent!.isEnabled = false;
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le membre est maintenant désactivé et n’a plus accès à la plateforme.");
        },
        error: (error) => {
          console.error('Error making agent disabled:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError('Erreur lors de la modification de la visibilité');
        }
      });
    } else {
      this.userService.enableUser(this.agent.id).subscribe({
        next: () => {
          this.agent!.isEnabled = true;
          this.isUpdatingVisibility = false;
          this.notification.showSuccess("Le membre est maintenant actif et peut de nouveau accéder à la plateforme.");
        },
        error: (error) => {
          console.error('Error making agent enabled:', error);
          this.isUpdatingVisibility = false;
          this.notification.showError('Erreur lors de la modification de la visibilité');
        }
      });
    }
  }

  openAvailabilityModal() {
    this.showAvailabilityModal = true;
  }

  closeAvailabilityModal() {
    this.showAvailabilityModal = false;
  }

  confirmAvailabilityChange() {
    this.closeAvailabilityModal();
    this.toggleAvailability();
  }

  toggleAvailability(): void {
    if (!this.agent || this.isUpdatingAvailability) return;

    this.isUpdatingAvailability = true;
    this.menuStateService.setActiveMenu(null);
    this.menuOpen = false;

    const action$ = this.agent.isAvailable
      ? this.userService.disableUser(this.agent.id)
      : this.userService.enableUser(this.agent.id);

    action$.subscribe({
      next: () => {
        this.agent!.isAvailable = !this.agent!.isAvailable;
        this.isUpdatingAvailability = false;

        const message = this.agent!.isAvailable
          ? "L’agent est maintenant disponible et peut être assigné à des missions."
          : "L’agent est désormais indisponible et ne pourra plus être assigné à des missions.";

        this.notification.showSuccess(message);
      },
      error: (error) => {
        console.error('Error updating agent availability:', error);
        this.isUpdatingAvailability = false;
        this.notification.showError(
          "Une erreur est survenue lors de la mise à jour de la disponibilité de l’agent."
        );
      }
    });
  }

  confirmDelete(): void {
    this.menuOpen = false;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
  }

  deleteAgent(): void {
    if(!this.agent){return}
    this.agentService.deleteAgent(this.agent.id).subscribe({
      next: () => {
        this.showDeleteModal = false;
        this.goToList();
      },
      error: (error) => {
        console.error('Error deleting agent:', error);
        this.notification.showError('Erreur lors de la suppression d\'agent');
        this.showDeleteModal = false;
      }
    });
  }

  getStars(rating: number): string[] {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(i <= rating ? './assets/images/icons/star.png' : './assets/images/icons/empty-star.png');
    }
    return stars;
  }

  edit(){
    if (this.agent) {
      this.router.navigate(['/admin/agents/edit', this.agentId]);
    }
  }
}